import { cn } from "@moe/priv/utils";
import { useDataContext } from "@web/route-services/chat/data-context";
import { Header } from "@web/route-services/chat/Header";
import { Message } from "@web/route-services/chat/message/Message";
import { useSwitchOnArrow } from "@web/route-services/chat/message/use-switch-on-arrow";
import { useServiceContext } from "@web/route-services/chat/service-context";
import { useChatScroll } from "@web/route-services/chat/use-chat-scroll";
import { useHistoryQuery } from "@web/route-services/chat/use-history-query";
import { useAppContext } from "@web/route-services/root/AppContext";
import { useCallback, useMemo, useRef } from "react";

export const historyPageSize = 30;

export const History = ({ globalHistoryRef }: { globalHistoryRef: React.MutableRefObject<Element | null> }) => {
  const { chatID, chars } = useDataContext();
  const { flagRef } = useServiceContext();
  const { isDesktopScreen } = useAppContext();
  const historyRef = useRef<Element | null>(null);
  const scrollHeightRef = useRef<number | undefined>(undefined);

  const callbackRef = useCallback(
    (node: any) => {
      if (!node) return;
      if (isDesktopScreen) {
        historyRef.current = node;
        globalHistoryRef.current = node;
        return;
      }
      historyRef.current = document.scrollingElement;
      globalHistoryRef.current = document.scrollingElement;
    },
    [globalHistoryRef, isDesktopScreen]
  );

  // Replace the existing useInfiniteQuery with:
  const { data: historyPages, isPending, fetchPreviousPage, hasPreviousPage } = useHistoryQuery(chatID);
  const history = useMemo(() => historyPages?.pages.flatMap((page) => page) || [], [historyPages]);

  const { topSentinelRef, bottomSentinelRef } = useChatScroll({
    history: history,
    historyRef,
    scrollHeightRef,
    isLoading: isPending,
    scrolledToTopHandler: () => {
      if (!hasPreviousPage) return;
      // Store current scroll height so we could scroll to the same position after fetching
      if (historyRef.current) scrollHeightRef.current = historyRef.current.scrollHeight;
      // Flag for scroll processing
      flagRef.current.fetchedPreviousPage = true;
      fetchPreviousPage();
    }
  });

  useSwitchOnArrow();

  return (
    <div className="relative flex h-full w-full grow overflow-hidden">
      <div className="relative flex h-full flex-auto flex-col pt-8 md:pl-8">
        <Header isHistoryPending={isPending} />
        <div
          ref={callbackRef}
          className={cn(
            "scroll-primary relative mb-24 flex grow flex-col overflow-x-clip pl-2.5 pr-2.5 pt-10 transition duration-500 ease-out md:pt-14",
            "md:mb-0 md:mr-2 md:overflow-y-scroll md:px-5",
            "pb-16 md:pb-32"
          )}
        >
          <div ref={topSentinelRef} className="w-full" />
          {history?.map((message, idx, arr) => {
            const id = Array.isArray(message) ? message[0].id : message.id;

            // If this is a user message in a group chat, find the name of the most recent character message above it.
            let charName: string | undefined = undefined;
            if (!Array.isArray(message)) {
              for (let j = idx - 1; j >= 0; j--) {
                const prev = arr[j];
                if (Array.isArray(prev)) {
                  const charID = prev[0].characterID;
                  charName = chars.get(charID)?.name;
                  if (charName) break;
                }
              }
            }

            return (
              <Message
                key={id}
                message={message}
                isTop={idx === 0}
                isBottom={idx === arr.length - 1}
                charName={charName}
                /* Space between messages except on the last message */
                /* Because of the sentinels, we can't use space-x/space-y*/
                className={`${idx < arr.length - 1 ? "mb-4" : "mb-8"}`}
              />
            );
          })}
          <div ref={bottomSentinelRef} className="w-full" />
        </div>
      </div>
    </div>
  );
};
